
import { useState, useEffect, useMemo } from "react";
import { Course, BirdieRecord, TeeBox, User } from "@/types";
import { DateSelector } from "@/components/birdie/DateSelector";
import { TeeBoxSelector } from "@/components/birdie/TeeBoxSelector";
import { NotesField } from "@/components/birdie/NotesField";

import { FormActions } from "@/components/birdie/FormActions";
import { GolferDropdown } from "@/components/birdie/GolferDropdown";
import { PlayerPresenceSelector } from "@/components/birdie/PlayerPresenceSelector";
import { ScoreTypeSelector } from "@/components/birdie/ScoreTypeSelector";
import { useAuth } from "@/context/AuthContext";
import { getScorePresencePlayers } from "@/integrations/supabase/scores";

interface BirdieFormProps {
  course: Course;
  userId: string;
  existingRecord?: BirdieRecord | null;
  isLoading?: boolean;
  gamePlayers?: User[];
  onCancel: () => void;
  onSubmit: (
    teeBox: TeeBox,
    date: string,
    notes: string,
    gameIds: string[],
    selectedUserId: string,
    gamePlayers: Record<string, User[]>,
    presentPlayers: User[],
    scoreType: BirdieRecord['scoreType']
  ) => void;
}

const BirdieForm = ({
  course,
  userId,
  existingRecord,
  isLoading = false,
  gamePlayers = [],
  onCancel,
  onSubmit,
}: BirdieFormProps) => {
  const { user } = useAuth();

  // Form state
  const [teeBox, setTeeBox] = useState<TeeBox>("white");
  const [date, setDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState<string>("");
  const [scoreType, setScoreType] = useState<"birdie" | "eagle" | "double_eagle">("birdie");
  const [selectedUserId, setSelectedUserId] = useState<string>(
    userId === "all" || userId === "current" ? "current" : userId
  );

  // Player presence state
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<string[]>([]);
  const [loadingPresence, setLoadingPresence] = useState(false);

  // Build allPlayers: gamePlayers + current user (if not already included)
  const allPlayers = useMemo(() => {
    const players = [...gamePlayers];
    if (user?.id && !players.some(p => p.id === user.id)) {
      players.push({
        id: user.id,
        name: user.user_metadata?.name || "You",
        email: user.email,
        imageUrl: undefined,
      });
    }
    return players;
  }, [gamePlayers, user]);

  // On mount or when allPlayers change: auto-select all for new scores
  useEffect(() => {
    if (!existingRecord && allPlayers.length > 0) {
      setSelectedPlayerIds(allPlayers.map(p => p.id));
    }
  }, [allPlayers, existingRecord]);

  // For editing: fetch presence from the existing score
  useEffect(() => {
    if (!existingRecord?.id) return;

    const fetchPresence = async () => {
      setLoadingPresence(true);
      try {
        const presentPlayers = await getScorePresencePlayers(existingRecord.id);
        if (presentPlayers.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setSelectedPlayerIds(presentPlayers.map((p: any) => p.id).filter(Boolean));
        }
      } catch (error) {
        console.error("Error fetching score presence:", error);
      } finally {
        setLoadingPresence(false);
      }
    };
    fetchPresence();
  }, [existingRecord?.id]);

  // Set form values from existing record
  useEffect(() => {
    if (existingRecord) {
      setTeeBox(existingRecord.teeBox);
      setDate(existingRecord.date);
      setNotes(existingRecord.notes || "");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setScoreType((existingRecord as any).scoreType || "birdie");
    }
  }, [existingRecord]);

  const handlePlayerToggle = (playerId: string) => {
    if (playerId === user?.id) return; // Current user always present
    setSelectedPlayerIds(prev =>
      prev.includes(playerId)
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId]
    );
  };

  const getSelectedPlayers = (): User[] => {
    return allPlayers.filter(p => selectedPlayerIds.includes(p.id));
  };

  const handleSubmit = () => {
    onSubmit(
      teeBox,
      date,
      notes,
      [], // gameIds — determined server-side now
      selectedUserId,
      {}, // gamePlayers map — no longer needed
      getSelectedPlayers(),
      scoreType
    );
  };

  return (
    <div className="space-y-4">
      <DateSelector value={date} onChange={(newDate) => setDate(newDate)} />

      <ScoreTypeSelector value={scoreType} onChange={setScoreType} />

      <TeeBoxSelector
        teeOptions={course.teeOptions}
        value={teeBox}
        onChange={setTeeBox}
      />

      {userId === "all" && (
        <GolferDropdown
          members={allPlayers}
          selectedUserId={selectedUserId}
          setSelectedUserId={setSelectedUserId}
        />
      )}

      <PlayerPresenceSelector
        players={allPlayers}
        selectedPlayerIds={selectedPlayerIds}
        onPlayerToggle={handlePlayerToggle}
        currentUserId={user?.id}
        isLoading={loadingPresence}
      />

      <NotesField value={notes} onChange={setNotes} />

      <FormActions
        isLoading={isLoading}
        isEditing={!!existingRecord}
        onCancel={onCancel}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default BirdieForm;
